import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  list: { maxHeight: "46vh", overflow: "auto", marginTop: theme.spacing(1) },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1),
    border: `1px solid ${theme.app.border}`,
    borderRadius: 8,
    marginBottom: 6,
  },
  rowMain: { flex: 1, minWidth: 0 },
  name: { fontWeight: 600, fontSize: 13 },
  body: { fontSize: 12, color: theme.app.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  badge: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12, color: "#fff" },
  hint: { color: theme.app.textTertiary, fontSize: 13, padding: theme.spacing(3, 1), textAlign: "center" },
  form: { marginTop: theme.spacing(1) },
}));

const STATUS_COLOR = {
  APPROVED: "#1E7E34",
  PENDING: "#B26A00",
  REJECTED: "#B12A2A",
  FAILED: "#B12A2A",
};

const GupshupTemplates = ({ open, connectorId, onClose }) => {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    elementName: "",
    category: "UTILITY",
    languageCode: "pt_BR",
    content: "",
    header: "",
    footer: "",
  });
  const [varSamples, setVarSamples] = useState([]); // sample value per {{n}}

  // how many {{n}} variables are in the content
  const varCount = body => {
    const nums = (String(body || "").match(/\{\{\s*(\d+)\s*\}\}/g) || []).map(s =>
      parseInt(s.replace(/\D/g, ""), 10)
    );
    return nums.length ? Math.max(...nums) : 0;
  };
  const nVars = varCount(form.content);
  // full example = content with each {{n}} replaced by its sample value
  const buildExample = () =>
    String(form.content).replace(/\{\{\s*(\d+)\s*\}\}/g, (_, d) => varSamples[Number(d) - 1] || "exemplo");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connectors/gupshup/templates", {
        params: connectorId ? { connectorId } : {},
      });
      setTemplates(data.templates || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [connectorId]);

  useEffect(() => {
    if (open) {
      setShowForm(false);
      load();
    }
  }, [open, load]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Meta template rules checked before submitting (avoid a guaranteed FAILED).
  const validate = () => {
    const c = (form.content || "").trim();
    if (!form.elementName.trim() || !c) return "Preencha o nome e o conteúdo do template.";
    if (/^\{\{\s*\d+\s*\}\}/.test(c))
      return "O conteúdo não pode COMEÇAR com uma variável. Coloque um texto antes (ex.: 'Olá {{1}}').";
    if (/\{\{\s*\d+\s*\}\}$/.test(c))
      return "O conteúdo não pode TERMINAR com uma variável. Coloque um texto depois (ex.: '{{1}}, seja bem-vindo!').";
    // variables must be 1,2,3… in order without gaps
    const nums = (c.match(/\{\{\s*(\d+)\s*\}\}/g) || []).map(s => parseInt(s.replace(/\D/g, ""), 10));
    const uniq = [...new Set(nums)].sort((a, b) => a - b);
    if (uniq.some((n, i) => n !== i + 1))
      return "As variáveis devem ser {{1}}, {{2}}, {{3}}… em ordem, sem pular números.";
    return null;
  };

  const create = async () => {
    const problem = validate();
    if (problem) {
      toast.warn(problem);
      return;
    }
    setCreating(true);
    try {
      await api.post("/connectors/gupshup/templates", {
        ...form,
        example: buildExample(),
        connectorId,
      });
      toast.success("Template enviado para aprovação.");
      setShowForm(false);
      setForm({ elementName: "", category: "UTILITY", languageCode: "pt_BR", content: "", header: "", footer: "" });
      setVarSamples([]);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const remove = async name => {
    try {
      await api.delete(`/connectors/gupshup/templates/${encodeURIComponent(name)}`, {
        params: connectorId ? { connectorId } : {},
      });
      setTemplates(list => list.filter(t => t.elementName !== name));
    } catch (err) {
      toastError(err);
    }
  };

  const retry = async name => {
    try {
      await api.post(`/connectors/gupshup/templates/${encodeURIComponent(name)}/retry`, { connectorId });
      toast.success("Template reenviado para aprovação.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1 }}>Templates do WhatsApp</span>
          {!showForm && (
            <IconButton size="small" title="Atualizar status" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        {showForm ? (
          <div className={classes.form}>
            <TextField
              label="Nome (elementName)"
              helperText="Sem espaços/acentos. Ex: confirmacao_pedido"
              value={form.elementName}
              onChange={e => setField("elementName", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
              fullWidth
              margin="dense"
            />
            <TextField select label="Categoria" value={form.category} onChange={e => setField("category", e.target.value)} fullWidth margin="dense">
              <MenuItem value="UTILITY">Utilidade (transacional)</MenuItem>
              <MenuItem value="MARKETING">Marketing</MenuItem>
              <MenuItem value="AUTHENTICATION">Autenticação (código)</MenuItem>
            </TextField>
            <TextField select label="Idioma" value={form.languageCode} onChange={e => setField("languageCode", e.target.value)} fullWidth margin="dense">
              <MenuItem value="pt_BR">Português (BR)</MenuItem>
              <MenuItem value="en_US">Inglês (US)</MenuItem>
              <MenuItem value="es">Espanhol</MenuItem>
            </TextField>
            <TextField label="Cabeçalho (opcional)" value={form.header} onChange={e => setField("header", e.target.value)} fullWidth margin="dense" />
            <TextField
              label="Conteúdo"
              helperText="Use {{1}}, {{2}}… para variáveis (em ordem). Não comece nem termine o texto com variável. Ex: Olá {{1}}, seu pedido {{2}} foi confirmado."
              value={form.content}
              onChange={e => setField("content", e.target.value)}
              fullWidth
              multiline
              minRows={3}
              margin="dense"
            />
            {nVars > 0 && (
              <>
                {Array.from({ length: nVars }).map((_, i) => (
                  <TextField
                    key={i}
                    label={`Exemplo para {{${i + 1}}}`}
                    helperText={i === 0 ? "Valor de amostra (a Meta usa pra revisar)" : ""}
                    value={varSamples[i] || ""}
                    onChange={e => {
                      const v = e.target.value;
                      setVarSamples(arr => {
                        const next = [...arr];
                        next[i] = v;
                        return next;
                      });
                    }}
                    fullWidth
                    margin="dense"
                  />
                ))}
                <Typography variant="caption" style={{ display: "block", color: "#666", margin: "4px 0" }}>
                  Pré-visualização: {buildExample()}
                </Typography>
              </>
            )}
            <TextField label="Rodapé (opcional)" value={form.footer} onChange={e => setField("footer", e.target.value)} fullWidth margin="dense" />
          </div>
        ) : loading ? (
          <div className={classes.hint}><CircularProgress size={26} /></div>
        ) : templates.length === 0 ? (
          <Typography className={classes.hint}>Nenhum template ainda. Crie o primeiro.</Typography>
        ) : (
          <div className={classes.list}>
            {templates.map(t => (
              <div key={t.id || t.elementName} className={classes.row}>
                <div className={classes.rowMain}>
                  <div className={classes.name}>
                    {t.elementName}{" "}
                    <span style={{ color: "#999", fontWeight: 400 }}>· {t.category} · {t.languageCode}</span>
                  </div>
                  <div className={classes.body}>{t.data}</div>
                  {(t.status === "FAILED" || t.status === "REJECTED") && t.reason && (
                    <div style={{ fontSize: 11, color: "#B12A2A", marginTop: 2 }}>
                      Motivo: {t.reason}
                    </div>
                  )}
                </div>
                <span className={classes.badge} style={{ background: STATUS_COLOR[t.status] || "#777" }}>
                  {t.status === "FAILED" ? "FALHOU" : t.status}
                </span>
                {(t.status === "FAILED" || t.status === "REJECTED") && (
                  <IconButton size="small" title="Reenviar para aprovação" onClick={() => retry(t.elementName)}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" title="Excluir" onClick={() => remove(t.elementName)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {showForm ? (
          <>
            <Button onClick={() => setShowForm(false)}>Voltar</Button>
            <Button onClick={create} variant="contained" color="primary" disabled={creating}>
              {creating ? "Enviando…" : "Enviar para aprovação"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Fechar</Button>
            <Button onClick={() => setShowForm(true)} variant="contained" color="primary" startIcon={<AddIcon />}>
              Novo template
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GupshupTemplates;
